import api from "../../../services/baseapi";

const BASE_URL = "/data-cleaning";

/* ── Upload ── */

export const uploadInternalDataset = async ({
  file,
  projectId,
  surveyId,
}: {
  file: File;
  projectId: string;
  surveyId?: string;
}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("project_id", projectId);
  if (surveyId) formData.append("survey_id", surveyId);

  const { data } = await api.post(
    `${BASE_URL}/datasets/internal-upload`,
    formData,
    { headers: { Accept: "application/json" } },
  );
  return data;
};

/* ── Dataset list  ── */

// GET /data-cleaning/datasets?project_id=&page=&page_size=
// Returns { total, page, page_size, items: [{id, name}] }
export const getDatasets = async (
  projectId: string,
  page = 1,
  pageSize = 10,
) => {
  const { data } = await api.get(`${BASE_URL}/datasets`, {
    params: { project_id: projectId, page, page_size: pageSize },
  });
  return data;
};

/* ── Delete ── */

export const deleteDataset = async (datasetId: string) => {
  const { data } = await api.delete(`${BASE_URL}/datasets/${datasetId}`);
  return data;
};

/* ── Reset dataset to original upload ── */

export const resetDataset = async (datasetId: string) => {
  const { data } = await api.post(`${BASE_URL}/datasets/${datasetId}/reset`);
  return data;
};

/* ── Quality Summary  ── */

// GET /data-cleaning/datasets/{id}/quality/summary
// Returns { total_rows, totals: {missing_values, inconsistencies, duplicates},
//           cards: [{key, label, issue_type, column_name, count}],
//           column_types: [{column_name, inferred_type}] }
export const getQualitySummary = async (datasetId: string) => {
  const { data } = await api.get(
    `${BASE_URL}/datasets/${datasetId}/quality/summary`,
  );
  return data;
};

/* ── Cleaning preview (data table)  ── */

// GET /data-cleaning/datasets/{id}/cleaning/preview?page=&page_size=
// Returns { dataset_id, dataset_name, summary_cards: {total_rows, missing_values,
//           inconsistencies, duplicates}, columns: [{column_name, inferred_type}],
//           rows: [{row_index, values:{...}, weight, is_promoted}],
//           total_rows, page, page_size }
export const getCleaningPreview = async (
  datasetId: string,
  page = 1,
  pageSize = 500,
) => {
  const { data } = await api.get(
    `${BASE_URL}/datasets/${datasetId}/cleaning/preview`,
    { params: { page, page_size: pageSize } },
  );
  return data;
};

/* ── Update one row  ── */

// PATCH /data-cleaning/datasets/{id}/cleaning/rows/{row_index}
// Body: { values: {...}, changed_columns?: string[] }
export const updateRow = async (
  datasetId: string,
  rowIndex: number,
  values: Record<string, any>,
  changedColumns?: string[],
) => {
  const { data } = await api.patch(
    `${BASE_URL}/datasets/${datasetId}/cleaning/rows/${rowIndex}`,
    { values, changed_columns: changedColumns },
  );
  return data;
};

/* ── Set / clear row weight  ── */

// PATCH /data-cleaning/datasets/{id}/cleaning/rows/{row_index}/weight
// Body: { weight: number | null }
export const setRowWeight = async (
  datasetId: string,
  rowIndex: number,
  weight: number | null,
) => {
  const { data } = await api.patch(
    `${BASE_URL}/datasets/${datasetId}/cleaning/rows/${rowIndex}/weight`,
    { weight },
  );
  return data;
};

/* ── Promote / un-promote (star)  ── */

// POST /data-cleaning/datasets/{id}/cleaning/rows/{row_index}/promote
export const promoteRow = async (datasetId: string, rowIndex: number) => {
  const { data } = await api.post(
    `${BASE_URL}/datasets/${datasetId}/cleaning/rows/${rowIndex}/promote`,
  );
  return data; // { row_index, is_promoted }
};

/* ── Quick Clean  ── */

// POST /data-cleaning/datasets/{id}/quick-clean/preview
// Body: { find_value: string, scope: string (column name or "__all__"), replace_with: string }
// Returns: { find_value, scope, replace_with, rows_affected, cells_affected,
//            before_after_preview: [{row_index, column_name, before, after}] }
export const previewQuickClean = async (
  datasetId: string,
  payload: { find_value: string; scope: string; replace_with: string },
) => {
  const { data } = await api.post(
    `${BASE_URL}/datasets/${datasetId}/quick-clean/preview`,
    payload,
  );
  return data;
};

// POST /data-cleaning/datasets/{id}/quick-clean/apply
// Same body as preview. Mutates the dataset.
// Returns: { message, impact: { rows_affected, cells_affected } }
export const applyQuickClean = async (
  datasetId: string,
  payload: { find_value: string; scope: string; replace_with: string },
) => {
  const { data } = await api.post(
    `${BASE_URL}/datasets/${datasetId}/quick-clean/apply`,
    payload,
  );
  return data;
};

/* ── List suggestions  ── */

// GET /data-cleaning/datasets/{id}/suggestions
// Returns { dataset_id, total, ai_included, suggestions: [...] }
export const getSuggestions = async (datasetId: string) => {
  const { data } = await api.get(
    `${BASE_URL}/datasets/${datasetId}/suggestions`,
  );
  return data;
};

/* ── Preview a suggestion  ── */

// POST /data-cleaning/datasets/{id}/suggestions/preview
// Body: { suggestion_id, rule_type, details }  (copy unchanged from the card)
// Returns: { rule_type, column_name, message, rows_affected, truncated,
//            changes: [{row_index, column_name, before, after}] }
export const previewSuggestionApi = async (
  datasetId: string,
  payload: {
    suggestion_id: string;
    rule_type: string;
    details: Record<string, any>;
  },
) => {
  const { data } = await api.post(
    `${BASE_URL}/datasets/${datasetId}/suggestions/preview`,
    payload,
  );
  return data;
};

/* ── Apply suggestion  ── */

// POST /data-cleaning/datasets/{id}/suggestions/apply
// Body: { suggestion_id, rule_type, details }  — always send all three
// Returns: { status, message, rule_type, column_name, rows_affected,
//            dataset_status, row_count, column_count, can_undo }
export const applySuggestion = async (
  datasetId: string,
  payload: {
    suggestion_id: string;
    rule_type: string;
    details: Record<string, any>;
  },
) => {
  const { data } = await api.post(
    `${BASE_URL}/datasets/${datasetId}/suggestions/apply`,
    payload,
  );
  return data;
};

/* ── Reject suggestion  ── */

// POST /data-cleaning/datasets/{id}/suggestions/reject
// Reject/remove a suggestion so it won't appear again
export const rejectSuggestion = async (
  datasetId: string,
  payload: {
    suggestion_id: string;
    rule_type: string;
    column_name: string;
    to_value: string;
    summary: string;
    source: string;
  },
) => {
  const { data } = await api.post(
    `${BASE_URL}/datasets/${datasetId}/suggestions/reject`,
    payload,
  );
  return data;
};

/* ── Changelog (Recent changes)  ── */

// GET /data-cleaning/datasets/{id}/changelog
// Returns array of { id, change_type, summary_json, impact_json, created_at }
export const getChangelog = async (datasetId: string) => {
  const { data } = await api.get(`${BASE_URL}/datasets/${datasetId}/changelog`);
  return data;
};

/* ── Undo  ── */

export const undoLastChange = async (datasetId: string) => {
  const { data } = await api.post(`${BASE_URL}/datasets/${datasetId}/undo`);
  return data;
};

/* ── Export / download  ── */

// GET /data-cleaning/datasets/{id}/export?format=csv
export const exportDataset = async (
  datasetId: string,
  format: "csv" | "excel" = "csv",
) => {
  const response = await api.get(`${BASE_URL}/datasets/${datasetId}/export`, {
    params: { format },
    responseType: "blob",
  });
  return response.data;
};

// Alias used by DataReviewPage for "Download Cleaned Data"
export const downloadCleanedData = exportDataset;

/* ── Recode operations  ── */

// GET /data-cleaning/datasets/{id}/column-stats?column=...
// Returns column profile: type, range, operations enabled/disabled, suggested output names
export const getColumnStats = async (
  datasetId: string,
  column: string,
  distinctLimit = 200,
) => {
  const { data } = await api.get(
    `${BASE_URL}/datasets/${datasetId}/column-stats`,
    { params: { column, distinct_limit: distinctLimit } },
  );
  return data;
};

// GET /data-cleaning/datasets/{id}/column-values?column=...
// Returns { column_name, row_count, null_count, total_distinct, truncated, is_numeric, values: [{value, count}] }
export const getColumnValues = async (
  datasetId: string,
  column: string,
  limit = 500,
  search?: string,
) => {
  const { data } = await api.get(
    `${BASE_URL}/datasets/${datasetId}/column-values`,
    { params: { column, limit, search } },
  );
  return data;
};

// POST /data-cleaning/datasets/{id}/recodes/preview
// Returns preview with before_after_preview, unmapped_values, distribution, etc.
export const previewRecode = async (
  datasetId: string,
  payload: {
    source_column: string;
    operation: string;
    definition: Record<string, any>;
    offset?: number;
    limit?: number;
  },
) => {
  const { data } = await api.post(
    `${BASE_URL}/datasets/${datasetId}/recodes/preview`,
    payload,
  );
  return data;
};

// POST /data-cleaning/datasets/{id}/recodes
// Applies the recode and saves it to changelog
export const applyRecode = async (
  datasetId: string,
  payload: {
    source_column: string;
    operation: string;
    definition: Record<string, any>;
    output_mode?: "new_column" | "in_place";
    output_column?: string;
    missing_codes?: string[];
    unmapped_policy?: string;
    keep_value_labels?: boolean;
    publish?: boolean;
  },
) => {
  const { data } = await api.post(
    `${BASE_URL}/datasets/${datasetId}/recodes`,
    payload,
  );
  return data;
};

/* ── Undo operations  ── */

// GET /data-cleaning/datasets/{id}/undo/preview?change_id=...
// Preview what will be reverted before applying undo
export const getUndoPreview = async (datasetId: string, changeId: string) => {
  const { data } = await api.get(
    `${BASE_URL}/datasets/${datasetId}/undo/preview`,
    { params: { change_id: changeId } },
  );
  return data;
};

// POST /data-cleaning/datasets/{id}/undo
// Apply undo and revert the change
export const applyUndo = async (datasetId: string, changeId: string) => {
  const { data } = await api.post(`${BASE_URL}/datasets/${datasetId}/undo`, {
    change_id: changeId,
  });
  return data;
};
